import { useState, type ChangeEvent, type MouseEvent } from "react";
import { Btn, Card, Icon, IconBtn, Input, Pill, T, tx } from "@v0y4ge/jrni-ui";
import type { UIEvent } from "../../lib/events-adapter";
import { toDate, toParts, friendlyTime } from "../../lib/datetime";
import { useNav } from "../../shell/router";
import { CreationStore } from "./creation/store";
import { useT, t as i18nT } from "../../lib/i18n";

/** Inline calendar for the Events page — Day · Week (7 days) · Month views.
 *  Click an empty slot to start a new event at that date+time (prefilled);
 *  the copy affordance on an event asks for a NEW date+time, then opens the
 *  create wizard cloned from that event. */

const _IC_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const _IC_MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const CAL_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DOW3 = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAL_MS_PER_DAY = 24 * 60 * 60 * 1000;

function _calDayIdxMon(d: Date): number { return (d.getDay() + 6) % 7; }
function _calWeekStart(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - _calDayIdxMon(x));
  return x;
}
function _calAddDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function _sameDay(a: Date, b: Date): boolean { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function _calIsoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - yearStart.getTime()) / CAL_MS_PER_DAY + 1) / 7);
}

function _parseEventWhen(ev: UIEvent): { date: Date; start: number; end: number } | null {
  const tz = ev.raw?.timezone || undefined;
  const startIso = ev.raw?.startsAt;
  if (startIso) {
    const p = toParts(startIso, tz);
    const ep = ev.raw?.endsAt ? toParts(ev.raw.endsAt, tz) : null;
    if (p) return { date: new Date(p.y, p.mo, p.d), start: p.h + p.mi / 60, end: ep ? ep.h + ep.mi / 60 : p.h + p.mi / 60 + 1 };
  }
  const base = toDate(ev.date);
  if (!base) return null;
  const [sTok, eTok] = String(ev.time || "").split("—").map((x) => x.trim());
  const sd = toDate(`${ev.date} · ${sTok}`) || base;
  const ed = eTok ? toDate(`${ev.date} · ${eTok}`) : null;
  const dec = (d: Date) => d.getHours() + d.getMinutes() / 60;
  const start = dec(sd);
  return { date: new Date(base.getFullYear(), base.getMonth(), base.getDate()), start, end: ed ? dec(ed) : start + 1 };
}
function _fmtHM(v: number): string {
  const d = new Date();
  d.setHours(Math.floor(v), Math.round((v % 1) * 60), 0, 0);
  return friendlyTime(d);
}
/** The wizard's legacy date-string shape ("Tue 16 Jul 2026 · 14:30"). */
function _wizardWhen(d: Date, hourDec: number): string {
  const hh = String(Math.floor(hourDec)).padStart(2, "0");
  const mm = String(Math.round((hourDec % 1) * 60)).padStart(2, "0");
  return `${DOW3[d.getDay()]} ${d.getDate()} ${_IC_MONTHS[d.getMonth()]} ${d.getFullYear()} · ${hh}:${mm}`;
}
const _pad2 = (n: number) => String(n).padStart(2, "0");

type CalView = "day" | "week" | "month";

export function EventsInlineCalendar({ events, onOpen, onOpenWeek, fill }: {
  events: UIEvent[];
  onOpen?: (ev: UIEvent) => void;
  onOpenWeek?: () => void;
  fill?: boolean;
}) {
  const t = useT();
  void onOpenWeek;
  const nav = useNav();
  const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19]; // ui-config
  const ROW_H = 44;
  const TIME_W = 56;
  const TODAY = new Date();
  const nowH = TODAY.getHours() + TODAY.getMinutes() / 60;

  const [view, setView] = useState<CalView>("week");
  const [cursor, setCursor] = useState<Date>(() => new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate()));
  // Clone flow: ask for the NEW date+time BEFORE opening the create wizard.
  const [cloneAsk, setCloneAsk] = useState<{ ev: UIEvent; date: string; time: string } | null>(null);

  const step = (dir: 1 | -1) => {
    if (view === "day") setCursor((c) => _calAddDays(c, dir));
    else if (view === "week") setCursor((c) => _calAddDays(c, dir * 7));
    else setCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1));
  };
  const jumpToday = () => setCursor(new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate()));

  const weekStart = _calWeekStart(cursor);
  const dayDates = view === "day" ? [cursor] : [0, 1, 2, 3, 4, 5, 6].map((i) => _calAddDays(weekStart, i));
  const cols = dayDates.length;

  const whenOf = new Map<string, { date: Date; start: number; end: number }>();
  events.forEach((ev) => { const w = _parseEventWhen(ev); if (w) whenOf.set(ev.id, w); });

  const placed = events.map((ev) => {
    const when = whenOf.get(ev.id);
    if (!when) return null;
    const dayIdx = dayDates.findIndex((d) => _sameDay(d, when.date));
    if (dayIdx < 0) return null;
    return { ev, dayIdx, start: when.start, end: when.end };
  }).filter((x): x is NonNullable<typeof x> => x !== null);

  const rangeLabel = view === "day"
    ? `${DOW3[cursor.getDay()]} ${cursor.getDate()} ${_IC_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "month"
      ? `${_IC_MONTHS_FULL[cursor.getMonth()]} ${cursor.getFullYear()}`
      : (() => {
        const a = dayDates[0]; const b = dayDates[6];
        return a.getMonth() === b.getMonth()
          ? `${a.getDate()}–${b.getDate()} ${_IC_MONTHS[a.getMonth()]} ${b.getFullYear()} · Week ${_calIsoWeek(a)}`
          : `${a.getDate()} ${_IC_MONTHS[a.getMonth()]} – ${b.getDate()} ${_IC_MONTHS[b.getMonth()]} ${b.getFullYear()} · Week ${_calIsoWeek(a)}`;
      })();

  /** Start the create wizard with the clicked date+time as the event start. */
  const createAt = (d: Date, hourDec: number) => {
    CreationStore.reset();
    CreationStore.setBlank();
    CreationStore.setField("start", _wizardWhen(d, hourDec));
    CreationStore.setField("end", _wizardWhen(d, Math.min(hourDec + 1, 23.5)));
    nav.go("create.basics");
  };

  /** Confirmed clone: prefill the wizard from the source event, then override
   *  the start to the chosen date+time (end keeps the source duration). */
  const cloneConfirmed = () => {
    if (!cloneAsk) return;
    const { ev, date, time } = cloneAsk;
    const [y, mo, d] = date.split("-").map(Number);
    const [h, mi] = time.split(":").map(Number);
    if (!y || !mo || !d || Number.isNaN(h)) return;
    const target = new Date(y, mo - 1, d);
    const startDec = h + (mi || 0) / 60;
    const when = whenOf.get(ev.id);
    const dur = when ? Math.max(0.5, when.end - when.start) : 1;
    CreationStore.loadEvent(ev as unknown as Record<string, unknown>); // prefill everything from the source
    CreationStore.endEditing();                                        // …but as a NEW event, not an edit
    CreationStore.setField("start", _wizardWhen(target, startDec));
    CreationStore.setField("end", _wizardWhen(target, Math.min(startDec + dur, 23.5)));
    setCloneAsk(null);
    nav.go("create.basics");
  };

  const openCloneAsk = (ev: UIEvent) => {
    const when = whenOf.get(ev.id);
    const d = when?.date || TODAY;
    const hh = when ? Math.floor(when.start) : 9;
    const mi = when ? Math.round((when.start % 1) * 60) : 0;
    setCloneAsk({ ev, date: `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`, time: `${_pad2(hh)}:${_pad2(mi)}` });
  };

  const viewBtn = (id: CalView, label: string) => {
    const on = view === id;
    return (
      <Btn key={id} type="button" onClick={() => setView(id)}
        style={{ height: "auto", borderRadius: 0, boxShadow: "none", padding: "5px 12px", border: "none", background: on ? T.p1 : T.white, color: on ? T.white : T.n2, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: "pointer", borderLeft: id !== "day" ? `1px solid ${T.border}` : "none" }}>{label}</Btn>
    );
  };

  // ── Month view grid (6×7): click a day → new event that day (09:00) ──────
  const monthGrid = () => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = _calWeekStart(first);
    const cells = Array.from({ length: 42 }, (_, i) => _calAddDays(gridStart, i));
    const byDay = new Map<string, UIEvent[]>();
    events.forEach((ev) => { const w = whenOf.get(ev.id); if (!w) return; const k = w.date.toDateString(); (byDay.get(k) || byDay.set(k, []).get(k)!).push(ev); });
    return (
      <div style={{ ...(fill ? { flex: 1, minHeight: 0, overflowY: "auto" } : {}) }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${T.border}`, background: T.white }}>
          {CAL_DAY_LABELS.map((l) => <div key={l} style={{ padding: "8px 8px", textAlign: "center", fontSize: 11, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04, borderLeft: l === "Mon" ? "none" : `1px solid ${T.border}` }}>{l}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = _sameDay(d, TODAY);
            const list = (byDay.get(d.toDateString()) || []).slice().sort((a, b) => (whenOf.get(a.id)?.start || 0) - (whenOf.get(b.id)?.start || 0));
            const shown = list.slice(0, 3);
            return (
              <div key={i} role="button" tabIndex={0} title={t("events.inlineCalendar.clickToAddAnEventOnThisDay", "Click to add an event on this day")}
                onClick={() => createAt(d, 9)}
                onKeyDown={(e) => { if (e.key === "Enter") createAt(d, 9); }}
                style={{ minHeight: 96, padding: 6, borderLeft: i % 7 === 0 ? "none" : `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, background: inMonth ? T.white : T.n6, cursor: "pointer", display: "flex", flexDirection: "column", gap: 3, overflow: "hidden" }}>
                <span style={{ alignSelf: "flex-start", fontSize: 12, fontWeight: 600, color: isToday ? T.white : inMonth ? T.n1 : T.soft, background: isToday ? T.p1 : "transparent", borderRadius: 999, minWidth: 22, height: 22, padding: "0 4px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{d.getDate()}</span>
                {shown.map((ev) => (
                  <div key={ev.id} onClick={(e) => { e.stopPropagation(); onOpen?.(ev); }}
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "2px 6px", borderRadius: 4, background: T.white, border: `1px solid ${T.border}`, borderLeft: `3px solid ${ev.hue}`, fontSize: 10.5, fontWeight: 600, color: T.n1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", cursor: "pointer" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</span>
                  </div>
                ))}
                {list.length > 3 ? <span style={{ fontSize: 10, color: T.soft, fontWeight: 600 }}>+{list.length - 3} {t("events.inlineCalendar.more", "more")}</span> : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Time grid (day + week views): click an empty slot → new event there ──
  const timeGrid = () => (
    <>
      <div style={{ display: "grid", gridTemplateColumns: `${TIME_W}px repeat(${cols}, 1fr)`, borderBottom: `1px solid ${T.border}`, background: T.white }}>
        <div />
        {dayDates.map((d, i) => {
          const isToday = _sameDay(d, TODAY);
          return (
            <div key={i} style={{ padding: "10px 8px", textAlign: "center", borderLeft: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 11, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04 }}>{CAL_DAY_LABELS[_calDayIdxMon(d)]}</div>
              <div style={{ marginTop: 2, fontSize: 15, fontWeight: 600, color: isToday ? T.white : T.n1, background: isToday ? T.p1 : "transparent", borderRadius: 999, width: 26, height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `${TIME_W}px repeat(${cols}, 1fr)`, position: "relative", ...(fill ? { flex: 1, minHeight: 0, overflowY: "auto" } : {}) }}>
        <div style={{ borderRight: `1px solid ${T.border}` }}>
          {HOURS.map((h) => (
            <div key={h} style={{ height: ROW_H, paddingTop: 4, paddingRight: 8, textAlign: "right", fontSize: 11, color: T.soft, fontVariantNumeric: "tabular-nums" }}>
              {h < 12 ? t('events.inlineCalendar.text8', { h }) : h === 12 ? t('events.inlineCalendar.text9') : /* TODO(i18n): replace template-literal with t('events.inlineCalendar.text10', { ... }) */`${h - 12}:00 pm`}
            </div>
          ))}
        </div>
        {dayDates.map((dayDate, dayIdx) => {
          const isToday = _sameDay(dayDate, TODAY);
          return (
            <div key={dayIdx} title={t("events.inlineCalendar.clickAnEmptySlotToAddAnEvent", "Click an empty slot to add an event")}
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                const y = e.clientY - rect.top + (e.currentTarget as HTMLDivElement).scrollTop;
                const hour = HOURS[0] + Math.max(0, Math.min(HOURS.length - 0.5, Math.floor((y / ROW_H) * 2) / 2)); // 30-min snap
                createAt(dayDate, hour);
              }}
              style={{ position: "relative", borderLeft: `1px solid ${T.border}`, cursor: "pointer" }}>
              {HOURS.map((h) => <div key={h} style={{ height: ROW_H, borderBottom: `1px solid ${T.border}` }} />)}
              {placed.filter((p) => p.dayIdx === dayIdx).map((p, i) => {
                const hourMin = HOURS[0]; const hourMax = HOURS[HOURS.length - 1] + 1;
                const cs = Math.max(hourMin, p.start); const ce = Math.min(hourMax, p.end);
                if (ce <= cs) return null;
                const top = (cs - hourMin) * ROW_H;
                const h = (ce - cs) * ROW_H - 4;
                const compact = h < 56;
                return (
                  <div key={i} onClick={(e) => { e.stopPropagation(); onOpen?.(p.ev); }}
                    style={{ position: "absolute", left: 3, right: 3, top, height: h, background: T.white, color: T.n1, border: `1px solid ${T.border}`, borderLeft: `4px solid ${p.ev.hue}`, borderRadius: 4, padding: compact ? "4px 8px" : "6px 8px", display: "flex", flexDirection: "column", gap: 2, overflow: "hidden", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.n1, lineHeight: 1.25, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>{p.ev.title}</span>
                      {p.ev.status === "live" ? <span style={{ width: 6, height: 6, borderRadius: 999, background: T.green1, flexShrink: 0 }} /> : null}
                      <Btn type="button" title={t("events.inlineCalendar.cloneToANewDateTime", "Clone to a new date & time")} aria-label={i18nT("events.inlineCalendar.clonePEvTitle.icu", { value0: p.ev.title })}
                        iconOnly={<Icon.copy size={12} />}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); openCloneAsk(p.ev); }}
                        style={{ height: "auto", width: "auto", borderRadius: 0, boxShadow: "none", background: "transparent", border: "none", padding: 0, color: T.soft, cursor: "pointer", display: "inline-flex", flexShrink: 0 }} />
                    </div>
                    {!compact ? <span style={{ fontSize: 10, color: T.soft, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{_fmtHM(p.start)} — {_fmtHM(p.end)}{p.ev.location ? /* TODO(i18n): replace template-literal with t('events.inlineCalendar.text11', { ... }) */` · ${p.ev.location.split(" · ").slice(-1)[0]}` : ""}{p.ev.registered ? /* TODO(i18n): replace template-literal with t('events.inlineCalendar.text12', { ... }) */` · ${p.ev.registered} guests` : ""}</span> : null}
                  </div>
                );
              })}
              {isToday && nowH >= HOURS[0] && nowH <= HOURS[HOURS.length - 1] + 1 ? (
                <div style={{ position: "absolute", left: 0, right: 0, top: (nowH - HOURS[0]) * ROW_H, height: 2, background: T.p1, zIndex: 1, pointerEvents: "none" }}>
                  <span style={{ position: "absolute", left: -48, top: -9, height: 18, padding: "0 6px", background: T.p1, color: T.white, fontSize: 10, fontWeight: 600, fontVariantNumeric: "tabular-nums", borderRadius: 3, display: "inline-flex", alignItems: "center" }}>{_fmtHM(nowH)}</span>
                  <span style={{ position: "absolute", left: -4, top: -3, width: 8, height: 8, borderRadius: 999, background: T.p1 }} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <Card style={{ padding: 0, overflow: "hidden", ...(fill ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : {}) }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h3 style={{ ...tx.h3, fontWeight: 600 }}>{rangeLabel}</h3>
          {view !== "month" ? <Pill tone="primary">{placed.length} {t("events.inlineCalendar.onGrid", "on grid")}</Pill> : null}
          {!_sameDay(cursor, TODAY) ? <Btn variant="link" onClick={jumpToday} style={{ height: "auto", padding: 0, fontSize: 12, fontWeight: 500 }}>{t("events.inlineCalendar.jumpToToday", "Jump to today")}</Btn> : null}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
            {viewBtn("day", "Day")}{viewBtn("week", "Week")}{viewBtn("month", "Month")}
          </div>
          <IconBtn label={i18nT("events.inlineCalendar.previousView.icu", { value0: view })} onClick={() => step(-1)}><Icon.chev dir="left" /></IconBtn>
          <IconBtn label={i18nT("events.inlineCalendar.nextView.icu", { value0: view })} onClick={() => step(1)}><Icon.chev dir="right" /></IconBtn>
        </div>
      </div>

      {view === "month" ? monthGrid() : timeGrid()}

      {/* Clone: ask for the new date & time BEFORE opening the create wizard */}
      {cloneAsk ? (
        <div role="dialog" aria-modal="true" aria-label={t("events.inlineCalendar.cloneEvent", "Clone event")}
          style={{ position: "fixed", inset: 0, background: "rgba(16,24,40,0.45)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setCloneAsk(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 400, maxWidth: "100%", background: T.white, borderRadius: 12, boxShadow: T.shadowLg, padding: 24, display: "flex", flexDirection: "column", gap: 16, fontFamily: T.font }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{t("events.inlineCalendar.clone", "Clone \"")}{" "}{cloneAsk.ev.title}"</div>
              <div style={{ fontSize: 12.5, color: T.soft, marginTop: 4, lineHeight: 1.5 }}>{t("events.inlineCalendar.pickTheNewDateAndStartTimeTheCreateWizardOpensPrefilled", "Pick the new date and start time — the create wizard opens prefilled from this event with the new schedule (same duration).")}</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 4 }} htmlFor="cal-clone-date">{t("events.inlineCalendar.newDate", "New date")}</label>
                <Input id="cal-clone-date" type="date" value={cloneAsk.date} onChange={(e: ChangeEvent<HTMLInputElement>) => setCloneAsk({ ...cloneAsk, date: e.target.value })} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 4 }} htmlFor="cal-clone-time">{t("events.inlineCalendar.newStartTime", "New start time")}</label>
                <Input id="cal-clone-time" type="time" value={cloneAsk.time} onChange={(e: ChangeEvent<HTMLInputElement>) => setCloneAsk({ ...cloneAsk, time: e.target.value })} />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn variant="secondary" size="sm" onClick={() => setCloneAsk(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn size="sm" icon={<Icon.copy size={13} />} disabled={!cloneAsk.date || !cloneAsk.time} onClick={cloneConfirmed}>{i18nT("events.inlineCalendar.cloneAmpOpenWizard.rendered", "Clone & open wizard")}</Btn>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
